import { useCallback, useEffect, useState } from 'react'
import type { InboxMessage } from '@/types/inbox'
import { MediaViewerDialog, type ViewerItem } from './MediaViewerDialog'
import { attachmentMeta, gridColumns } from './mediaGallery'
import { useMediaBlob } from './mediaBlob'
import { MEDIA_TEXT, thumbnailLabel } from './mediaViewText'

interface ImageGalleryProps {
  /** The raster images that load up front, two or more, in stored order. */
  images: readonly InboxMessage[]
  /** The message's whole viewer set (every viewable row), so a cell opens the dialog at its own place. */
  viewer: readonly ViewerItem[]
}

const COLS: Record<1 | 2 | 3, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' }

/**
 * SA-1094 (design note §4.4): several images under one message as a grid of
 * square cells. Each cell loads its bytes through the shared hold
 * (`mediaBlob.useMediaBlob`, one request per image however many hold it) and
 * reports them up, so the dialog opened from any cell shows the loaded ones
 * without minting.
 */
export function ImageGallery({ images, viewer }: ImageGalleryProps) {
  const [blobs, setBlobs] = useState<Record<string, string>>({})
  const [openAt, setOpenAt] = useState<number | null>(null)
  const onLoaded = useCallback((id: string, url: string | null) => {
    setBlobs(prev => {
      if ((prev[id] ?? null) === url) return prev
      const next = { ...prev }
      if (url) next[id] = url
      else delete next[id]
      return next
    })
  }, [])
  const cols = gridColumns(images.length)
  const items = viewer.map(it => (blobs[it.messageId] ? { ...it, objectUrl: blobs[it.messageId] } : it))

  return (
    <>
      <div data-testid="media-gallery" data-cols={cols} className={`grid gap-1.5 ${COLS[cols]}`}>
        {images.map(image => {
          const at = viewer.findIndex(it => it.messageId === image.id)
          return (
            <GalleryCell
              key={image.id}
              image={image}
              onLoaded={onLoaded}
              onOpen={at >= 0 ? () => setOpenAt(at) : null}
            />
          )
        })}
      </div>
      {openAt != null && (
        <MediaViewerDialog items={items} index={openAt} onClose={() => setOpenAt(null)} />
      )}
    </>
  )
}

const CELL = 'aspect-square rounded-lg border border-border'

function GalleryCell({ image, onLoaded, onOpen }: {
  image: InboxMessage
  onLoaded: (id: string, url: string | null) => void
  /** Null when the viewer has no item for this row (no viewable type): a plain thumbnail. */
  onOpen: (() => void) | null
}) {
  const { objectUrl, gone, error } = useMediaBlob(image.mediaUrl ?? '')
  const { filename } = attachmentMeta(image)
  // Report the bytes up as they arrive and as they go (unmount revokes them).
  useEffectReport(image.id, objectUrl, onLoaded)

  // SA-1110: a cell's lines are the lone image's words, inside the square,
  // so a gone or failed image never breaks the grid.
  if (gone || error) {
    return (
      <div data-testid="gallery-cell" className={`${CELL} bg-muted flex items-center justify-center p-2 text-center`}>
        <p className={`text-xs italic ${gone ? 'text-muted-foreground' : 'text-destructive'}`}>
          {gone ? MEDIA_TEXT.imageGone : MEDIA_TEXT.unavailable}
        </p>
      </div>
    )
  }
  if (!objectUrl) {
    return (
      // SA-1122: the cell says it is busy, read more reliably than the hidden line alone.
      <div data-testid="gallery-cell" aria-busy="true" className={`${CELL} bg-muted`}>
        <span className="sr-only">{MEDIA_TEXT.loading}</span>
      </div>
    )
  }
  if (!onOpen) {
    return (
      <div data-testid="gallery-cell" className={`${CELL} overflow-hidden`}>
        <img src={objectUrl} alt={MEDIA_TEXT.attachmentAlt} className="w-full h-full object-cover" />
      </div>
    )
  }
  return (
    <button
      type="button"
      data-testid="gallery-cell"
      onClick={onOpen}
      aria-label={thumbnailLabel(filename)}
      className={`${CELL} overflow-hidden cursor-zoom-in`}
    >
      <img src={objectUrl} alt="" className="w-full h-full object-cover" />
    </button>
  )
}

function useEffectReport(id: string, url: string | null, onLoaded: (id: string, url: string | null) => void) {
  useEffect(() => {
    onLoaded(id, url)
    return () => onLoaded(id, null)
  }, [id, url, onLoaded])
}
